// @flow

import React, { useCallback, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { flatten } from 'lodash';
import { randomAudioPlayer, shuffleArray } from '../utils';
import MemoryField from './NewMemoryField';
import type { AudioAssets, Card, CardsData } from './types';
import useGameState from '../utils/use_game_state';
import useWindowSize from '../utils/use_window_size';
import audioPlaylist from '../utils/audio_playlist';
import Game from '../Shared/Game';

type Props = {
  images: Array<string>,
  audio: AudioAssets,
  assetPath: String,
  relatedGames: Array<any>,
  isApp: Boolean
};

function createCardsData(allImages: Array<string>, level: number): CardsData {
  const levelImages = allImages.slice(0, level * 2);
  const cardPairs = levelImages.map((image, index) => [
    { imageUrl: image, pair: index },
    { imageUrl: image, pair: index },
  ]);
  const cards = flatten(cardPairs);
  const doubledCards = cards.map((card, index) => ({ ...card, id: index }));
  const shuffledCards = shuffleArray(doubledCards);
  if (level === 2) {
    shuffledCards.splice(4, 0, null);
  }
  return shuffledCards;
}

const MemoryGame = ({ images, audio, assetPath, relatedGames, isApp }: Props) => {
  const windowSize = useWindowSize();
  const [level, setLevel] = useState(1);
  const [cardsData, setCardsData] = useState(createCardsData(images, level));
  const [activeCards, setActiveCards] = useState([]);
  const [finishedCards, setFinishedCards] = useState([]);
  const loadAssets = useCallback(() => [
    // TODO: Use these playlists
    audioPlaylist(audio.right),
    audioPlaylist(audio.wrong),
  ], [audio]);
  const [gameState, setGameDone] = useGameState(loadAssets);

  const turnCard = (card: Card): void => {
    if (
      activeCards.length === 2 ||
      (activeCards[0] && activeCards[0].id === card.id)
    ) {
      return;
    }

    setActiveCards(activeCards.concat(card));
  };

  useEffect(() => {
    if (activeCards.length < 2) {
      return;
    }
    window.setTimeout(() => {
      if (activeCards[0].pair === activeCards[1].pair) {
        const newFinishedCards = finishedCards.concat([activeCards[0].id, activeCards[1].id]);
        if (newFinishedCards.length === cardsData.filter(i => i !== null).length) {
          if (level < 7) {
            setActiveCards([]);
            setFinishedCards([]);
            setCardsData(createCardsData(images, level + 1));
            setLevel(level + 1);
            const nextLevelAudio = new Audio(audio.harder);
            nextLevelAudio.play();
          } else {
            setActiveCards([]);
            setCardsData([]);
            const { right } = audio;
            randomAudioPlayer(right).play();
            setGameDone();
          }
        } else {
          const { right } = audio;
          randomAudioPlayer(right).play();
          setActiveCards([]);
          setFinishedCards(newFinishedCards);
        }
      } else {
        const { wrong } = audio;
        randomAudioPlayer(wrong).play();
        setActiveCards([]);
      }
    }, 1000);
  }, [activeCards, audio, cardsData, finishedCards, images, level, setGameDone]);

  const windowWidth = windowSize.width;
  const windowHeight = windowSize.height;
  // const incrementalWidthScale = [3, 3, 4, 6.5, 8.5, 9, 10, 10];
  const fieldHeight = windowHeight * 0.8;
  const fieldWidth = windowWidth * 0.9;
  const cardSize = Math.min(windowWidth / 6, windowHeight / 4);
  return (
    <Game gameState={gameState} relatedGames={relatedGames} assetPath={assetPath} isApp={isApp}>
      <MemoryField
        assetPath={assetPath}
        cards={cardsData}
        activeCards={activeCards}
        turnCard={turnCard}
        finishedCards={finishedCards}
        level={level}
        fieldWidth={fieldWidth}
        fieldHeight={fieldHeight}
        cardSize={cardSize}
      />
    </Game>
  );
};

const Memory = MemoryGame;
const data = document.querySelector('#memory').dataset;
const props = {
  back: data.back,
  title: data.title,
  images: JSON.parse(data.images),
  audio: JSON.parse(data.audio),
  assetPath: data.assetPath,
  relatedGames: JSON.parse(data.related),
  isApp: data.app === 'true',
};
ReactDOM.render(<Memory {...props} />, document.querySelector('#memory'));
